import { useMutation } from '@tanstack/react-query';
import { Link, Navigate, Outlet, Route, Routes } from 'react-router';
import { ApiClientError, post } from './lib/api';
import { useEnterKitchen, useMe } from './lib/queries';
import { PageLoader, SyncPill } from './components/ui';
import { Welcome } from './screens/Welcome';
import { NeedsYou, Today } from './screens/Today';
import { Subscribers } from './screens/Subscribers';
import { Profile } from './screens/Profile';
import { Pause } from './screens/Pause';
import { SubscriptionForm } from './screens/SubscriptionForm';
import { Kitchen } from './screens/Kitchen';
import { Menus } from './screens/Menus';
import { More } from './screens/More';
import { Search } from './screens/Search';
import { NewOrder, OrderDetails } from './screens/Orders';
import { Print } from './screens/Print';
import { MyPlan } from './screens/MyPlan';
import { PlanLink } from './screens/PlanLink';
import { Delivery } from './screens/Delivery';
import { Revenue } from './screens/Revenue';
import { Payment } from './screens/Payment';
import { Remind, RemindAll } from './screens/Remind';
import { Settings } from './screens/Settings';
import { Import } from './screens/Import';
import { Move } from './screens/Move';
import { Admin, AdminKitchen, AdminNewKitchen } from './screens/Admin';
import { Waiting } from './screens/Waiting';
import { RegistrationRequests, CustomerRegistration } from './screens/Registration';
import { Privacy } from './screens/Privacy';
import type { MeDto } from '@tiffinify/contracts';

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="app">{children}<SyncPill /></div>;
}

/** A Tiffinify team member helping in someone else's kitchen always sees whose it is, and the way back. */
function VisitBar() {
  const me = useMe().data;
  const enter = useEnterKitchen();
  const home = me?.kitchens.find((k) => k.id === me.user.returnKitchenId) ?? me?.kitchens[0];
  const exit = useMutation({
    mutationFn: () => post<MeDto>('/auth/switch', { kitchenId: home!.id }),
    onSuccess: enter,
  });
  if (!me?.user.visiting) return null;
  return (
    <div className="visit-bar" role="status">
      <span>Visiting {me.kitchen.name} as staff</span>
      {home && <button type="button" className="visit-exit" aria-label={`Exit visit and return to ${home.name}`} disabled={exit.isPending} onClick={() => exit.mutate()}>
        {exit.isPending ? 'Returning…' : 'Exit visit'}
      </button>}
      <Link to="/admin">Tiffinify team</Link>
      {exit.error && <small className="visit-bar-error">Could not return to {home?.name}. Please try again.</small>}
    </div>
  );
}

/** Everything inside needs a signed-in user; otherwise go to the welcome screen. */
function RequireAuth() {
  const me = useMe();
  if (me.isPending) return <Shell><PageLoader /></Shell>;
  if (me.error) {
    if (me.error instanceof ApiClientError && me.error.status === 401) return <Navigate to="/welcome" replace />;
    return <Shell><div className="center-screen">{me.error.message}</div></Shell>;
  }
  // A kitchen the owner signed up for waits for the Tiffinify team; until then this is all they see.
  const m = me.data;
  if ((m.kitchen.status ?? 'approved') !== 'approved' && !m.user.isTiffinifyTeam) return <Shell><Waiting me={m} /></Shell>;
  return <Shell><VisitBar /><Outlet /></Shell>;
}

/** The Tiffinify team's pages; anyone else is sent to Today (the API refuses them too). */
function RequireTiffinifyTeam() {
  const me = useMe();
  if (!me.data?.user.isTiffinifyTeam) return <Navigate to="/" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <Routes>
      <Route path="/welcome" element={<Shell><Welcome /></Shell>} />
      <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
      <Route path="/join/:token" element={<Shell><Welcome /></Shell>} />
      <Route path="/register/:token" element={<Shell><CustomerRegistration /></Shell>} />
      <Route path="/my/:token" element={<Shell><MyPlan /></Shell>} />
      <Route element={<RequireAuth />}>
        <Route index element={<Today />} />
        <Route path="subscribers" element={<Subscribers />} />
        <Route path="registrations" element={<RegistrationRequests />} />
        <Route path="s/:id" element={<Profile />} />
        <Route path="s/:id/pause" element={<Pause />} />
        <Route path="s/:id/move" element={<Move />} />
        <Route path="s/:id/plan-link" element={<PlanLink />} />
        <Route path="s/:id/renew" element={<SubscriptionForm mode="renew" />} />
        <Route path="s/:id/edit" element={<SubscriptionForm mode="edit" />} />
        <Route path="s/new" element={<Navigate to="/new" replace />} />
        <Route path="new" element={<SubscriptionForm mode="new" />} />
        <Route path="kitchen" element={<Kitchen />} />
        <Route path="menus" element={<Menus />} />
        <Route path="needs" element={<NeedsYou />} />
        <Route path="orders/new" element={<NewOrder />} />
        <Route path="orders/:id" element={<OrderDetails />} />
        <Route path="orders/:id/edit" element={<NewOrder />} />
        <Route path="more" element={<More />} />
        <Route path="search" element={<Search />} />
        <Route path="print" element={<Print />} />
        <Route path="delivery" element={<Delivery />} />
        <Route path="revenue" element={<Revenue />} />
        <Route path="pay/:subId" element={<Payment />} />
        <Route path="remind/:subId" element={<Remind />} />
        <Route path="remind-all" element={<RemindAll />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/:page" element={<Settings />} />
        <Route path="import" element={<Import />} />
        <Route element={<RequireTiffinifyTeam />}>
          <Route path="admin" element={<Admin />} />
          <Route path="admin/k/:id" element={<AdminKitchen />} />
          <Route path="admin/new" element={<AdminNewKitchen />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
